import { useEffect, useMemo, useState } from "react";
import { ExternalLink, MoreHorizontal, Pencil } from "lucide-react";
import { Handle, Position } from "@xyflow/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getPageBaseUrl } from "@/routes/pages/edit/pageEditorUtils";
import type { AutomationStepPage } from "@/types/Automation";
import type { StepNodeData } from "../types";

interface PageOption {
  _id: string;
  title?: string;
  route?: string;
  formHtmlIds?: string[];
}

const PAGE_NONE = "__none__";

export function PageStepFields({
  step,
  onPageStepChange,
}: {
  step: AutomationStepPage;
  onPageStepChange: StepNodeData["onPageStepChange"];
}) {
  const [pages, setPages] = useState<PageOption[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(true);
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const pageId = step.data?.pageId?.trim() || "";

  useEffect(() => {
    let cancelled = false;
    fetch("/api/pages")
      .then((res) => res.json())
      .then((payload) => {
        if (cancelled) return;
        const loaded = (payload.pages as PageOption[] | undefined) ?? [];
        setPages(loaded.filter((entry) => entry._id));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoadingPages(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  const selectedPage = pages.find((page) => page._id === pageId);
  const formHtmlIds = selectedPage?.formHtmlIds ?? [];
  const viewUrl = useMemo(
    () => `${getPageBaseUrl(customDomainHostname)}${selectedPage?.route || "/"}`,
    [customDomainHostname, selectedPage?.route],
  );

  return (
    <>
      <div
        className="p-4 nodrag"
        onPointerDownCapture={(e) => {
          if ((e.target as HTMLElement).closest(".react-flow__handle")) return;
          if ((e.target as HTMLElement).closest("[data-page-actions]")) return;
          e.stopPropagation();
        }}
      >
        <div className="flex items-center gap-1">
          <Select
            value={pageId || PAGE_NONE}
            onValueChange={(value) =>
              onPageStepChange({
                stepId: step.id,
                pageId: value === PAGE_NONE ? "" : value,
                formTargetStepIds: {},
              })
            }
          >
            <SelectTrigger className="min-w-0 flex-1 text-sm bg-white">
              <SelectValue placeholder={isLoadingPages ? "Loading..." : "Select a page"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PAGE_NONE}>Select a page</SelectItem>
              {pages.map((page) => (
                <SelectItem key={page._id} value={page._id}>
                  {page.title?.trim() || page._id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="nodrag nopan nowheel pointer-events-auto relative z-10 h-8 w-8 shrink-0"
                aria-label="Page actions"
                data-page-actions=""
                disabled={!pageId}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="nodrag nopan nowheel pointer-events-auto z-[2000]"
              onCloseAutoFocus={(e) => e.preventDefault()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() => window.open(viewUrl, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  window.open(`/pages/${pageId}/edit`, "_blank", "noopener,noreferrer")
                }
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {formHtmlIds.length > 0 ? (
        <div style={{ height: formHtmlIds.length * 28 }} />
      ) : null}
      <div className="absolute bottom-2 right-0 flex flex-col items-end gap-1 nodrag">
        {formHtmlIds.map((formHtmlId) => (
          <div key={formHtmlId} className="flex items-center">
            <span className="py-0.5 pr-2 rounded-md bg-white/80 text-xs font-semibold text-indigo-700 max-w-[180px] truncate">
              {formHtmlId}
            </span>
            <Handle
              id={formHtmlId}
              type="source"
              position={Position.Right}
              className="!bg-indigo-400 !w-3 !h-3 !border-2 !border-white"
            />
          </div>
        ))}
      </div>
    </>
  );
}
